import {
  AnalyticsService,
  type AnalyticsEventParams,
} from '@core/services/analytics/analytics-service'

const analyticsService = AnalyticsService.getInstance()

export const PiAnalyticsEvent = {
  screenView: 'pi_screen_view',
  screenTimeSpent: 'pi_screen_time_spent',
  modalView: 'pi_modal_view',
  navClick: 'pi_nav_click',
  externalLinkOpen: 'pi_external_link_open',
  searchRun: 'pi_search_run',
  filterApply: 'pi_filter_apply',
  filterClear: 'pi_filter_clear',
  loadMore: 'pi_load_more',
  contentOpen: 'pi_content_open',
  authLoginSuccess: 'pi_auth_login_success',
  authSignupSuccess: 'pi_auth_signup_success',
  authResetSuccess: 'pi_auth_reset_success',
  authLogout: 'pi_auth_logout',
  profileNameSave: 'pi_profile_name_save',
  profilePhotoUpload: 'pi_profile_photo_upload',
  newsFavoriteToggle: 'pi_news_favorite_toggle',
  newsSubmit: 'pi_news_submit',
  webinarSegmentChange: 'pi_webinar_segment_change',
  webinarRegisterClick: 'pi_webinar_register_click',
  webinarQuestionSubmit: 'pi_webinar_question_submit',
  webinarSubmit: 'pi_webinar_submit',
  webinarSuggestionSubmit: 'pi_webinar_suggestion_submit',
  hackSubmit: 'pi_hack_submit',
  hackWhatWorksSubmit: 'pi_hack_whatworks_submit',
  commentSubmit: 'pi_comment_submit',
  commentReplySubmit: 'pi_comment_reply_submit',
} as const

type PiAnalyticsEventName = (typeof PiAnalyticsEvent)[keyof typeof PiAnalyticsEvent]

const routeDefinitions = [
  { regex: /^\/$/, screenName: 'home', routeGroup: 'home' },
  { regex: /^\/about$/, screenName: 'about', routeGroup: 'about' },
  { regex: /^\/news$/, screenName: 'news_list', routeGroup: 'news' },
  { regex: /^\/favorites$/, screenName: 'user_favorites', routeGroup: 'user' },
  {
    regex: /^\/notifications$/,
    screenName: 'user_notifications',
    routeGroup: 'user',
  },
  { regex: /^\/news\/create$/, screenName: 'news_create', routeGroup: 'news' },
  { regex: /^\/news\/[^/]+\/edit$/, screenName: 'news_edit', routeGroup: 'news' },
  { regex: /^\/news\/[^/]+$/, screenName: 'news_detail', routeGroup: 'news' },
  {
    regex: /^\/webinars(?:\/latest|\/upcoming)?$/,
    screenName: 'webinars_list',
    routeGroup: 'webinars',
  },
  {
    regex: /^\/webinars\/suggestions$/,
    screenName: 'webinars_suggestions',
    routeGroup: 'webinars',
  },
  {
    regex: /^\/webinars\/create$/,
    screenName: 'webinars_create',
    routeGroup: 'webinars',
  },
  {
    regex: /^\/webinars\/[^/]+\/edit$/,
    screenName: 'webinars_edit',
    routeGroup: 'webinars',
  },
  {
    regex: /^\/webinars\/[^/]+$/,
    screenName: 'webinars_detail',
    routeGroup: 'webinars',
  },
  {
    regex: /^\/prof-bas-bloem-weekly$/,
    screenName: 'podcasts_list',
    routeGroup: 'podcasts',
  },
  {
    regex: /^\/prof-bas-bloem-weekly\/[^/]+$/,
    screenName: 'podcasts_detail',
    routeGroup: 'podcasts',
  },
  {
    regex: /^\/dr-michael-okun-my-take$/,
    screenName: 'instagram_reels_list',
    routeGroup: 'instagram_reels',
  },
  {
    regex: /^\/dr-michael-okun-my-take\/[^/]+$/,
    screenName: 'instagram_reels_detail',
    routeGroup: 'instagram_reels',
  },
  { regex: /^\/hacks$/, screenName: 'hacks_list', routeGroup: 'hacks' },
  { regex: /^\/hacks\/create$/, screenName: 'hacks_create', routeGroup: 'hacks' },
  { regex: /^\/hacks\/[^/]+\/edit$/, screenName: 'hacks_edit', routeGroup: 'hacks' },
  {
    regex: /^\/hacks\/[^/]+\/what-works\/[^/]+\/comments$/,
    screenName: 'hacks_comments',
    routeGroup: 'hacks',
  },
  { regex: /^\/hacks\/[^/]+$/, screenName: 'hacks_detail', routeGroup: 'hacks' },
  {
    regex: /^\/legal\/medical-disclaimer$/,
    screenName: 'legal_medical_disclaimer',
    routeGroup: 'legal',
  },
  {
    regex: /^\/legal\/privacy-policy$/,
    screenName: 'legal_privacy_policy',
    routeGroup: 'legal',
  },
  {
    regex: /^\/legal\/terms-of-service$/,
    screenName: 'legal_terms_of_service',
    routeGroup: 'legal',
  },
  {
    regex: /^\/legal\/cookie-policy$/,
    screenName: 'legal_cookie_policy',
    routeGroup: 'legal',
  },
  {
    regex: /^\/legal\/faqs$/,
    screenName: 'legal_faqs',
    routeGroup: 'legal',
  },
] as const

function normalizeScreenPath(path: string): string {
  const normalized = path.trim() || '/'
  return normalized.slice(0, 100)
}

function deriveFallbackRouteGroup(pathname: string): string {
  const firstSegment = pathname.split('/').filter(Boolean)[0]
  if (!firstSegment) {
    return 'root'
  }
  return firstSegment.slice(0, 40)
}

function buildBaseParams(
  screenPath: string,
  isAuthenticated?: boolean,
): AnalyticsEventParams {
  const pathname = normalizeScreenPath(screenPath).split('?')[0]
  const matchedRoute = routeDefinitions.find((entry) => entry.regex.test(pathname))

  return {
    screen_name: matchedRoute?.screenName ?? 'unknown_screen',
    screen_path: normalizeScreenPath(screenPath),
    route_group: matchedRoute?.routeGroup ?? deriveFallbackRouteGroup(pathname),
    is_authenticated: isAuthenticated,
  }
}

function toTrackableParams(
  baseParams: AnalyticsEventParams,
  extraParams?: AnalyticsEventParams,
): AnalyticsEventParams {
  return {
    ...baseParams,
    ...(extraParams ?? {}),
  }
}

export class AnalyticsFeature {
  private static track(
    eventName: PiAnalyticsEventName,
    params: AnalyticsEventParams = {},
  ): void {
    void analyticsService.logEventSafe(eventName, params)
  }

  public static trackScreenView(
    screenPath: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.screenView,
      buildBaseParams(screenPath, isAuthenticated),
    )
  }

  public static trackScreenTimeSpent(
    screenPath: string,
    dwellSeconds: number,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.screenTimeSpent,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        dwell_sec: Number(dwellSeconds.toFixed(1)),
      }),
    )
  }

  public static trackModalView(
    modalName: string,
    screenPath: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.modalView,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        modal_name: modalName,
      }),
    )
  }

  public static trackNavClick(
    screenPath: string,
    destination: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.navClick,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: 'header',
        content_type: 'navigation',
        content_id: destination,
      }),
    )
  }

  public static trackExternalLinkOpen(
    screenPath: string,
    source: string,
    contentType: string,
    contentId?: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.externalLinkOpen,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_type: contentType,
        content_id: contentId,
      }),
    )
  }

  public static trackSearchRun(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.searchRun,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackFilterApply(
    screenPath: string,
    source: string,
    contentId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.filterApply,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_id: contentId,
      }),
    )
  }

  public static trackFilterClear(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.filterClear,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackLoadMore(
    screenPath: string,
    source: string,
    contentType: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.loadMore,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_type: contentType,
      }),
    )
  }

  public static trackContentOpen(
    screenPath: string,
    contentType: string,
    contentId: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.contentOpen,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_type: contentType,
        content_id: contentId,
      }),
    )
  }

  public static trackNewsFavoriteToggle(
    screenPath: string,
    source: string,
    contentId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.newsFavoriteToggle,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_type: 'news',
        content_id: contentId,
      }),
    )
  }

  public static trackNewsSubmit(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.newsSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackWebinarSegmentChange(
    screenPath: string,
    segment: 'latest' | 'upcoming',
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.webinarSegmentChange,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        content_type: 'webinar_segment',
        content_id: segment,
      }),
    )
  }

  public static trackWebinarRegisterClick(
    screenPath: string,
    webinarId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.webinarRegisterClick,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: 'webinar_detail',
        content_type: 'webinar',
        content_id: webinarId,
      }),
    )
  }

  public static trackWebinarQuestionSubmit(
    screenPath: string,
    webinarId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.webinarQuestionSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: 'webinar_detail',
        content_type: 'webinar',
        content_id: webinarId,
      }),
    )
  }

  public static trackWebinarSubmit(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.webinarSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackWebinarSuggestionSubmit(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.webinarSuggestionSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackHackSubmit(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.hackSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackHackWhatWorksSubmit(
    screenPath: string,
    symptomId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.hackWhatWorksSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: 'hack_detail',
        content_type: 'hack_symptom',
        content_id: symptomId,
      }),
    )
  }

  public static trackCommentSubmit(
    screenPath: string,
    source: string,
    contentType: string,
    contentId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.commentSubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_type: contentType,
        content_id: contentId,
      }),
    )
  }

  public static trackCommentReplySubmit(
    screenPath: string,
    source: string,
    contentType: string,
    contentId: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.commentReplySubmit,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
        content_type: contentType,
        content_id: contentId,
      }),
    )
  }

  public static trackAuthLoginSuccess(
    screenPath: string,
    source: string,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.authLoginSuccess,
      toTrackableParams(buildBaseParams(screenPath, true), {
        action_source: source,
      }),
    )
  }

  public static trackAuthSignupSuccess(
    screenPath: string,
    source: string,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.authSignupSuccess,
      toTrackableParams(buildBaseParams(screenPath, true), {
        action_source: source,
      }),
    )
  }

  public static trackAuthResetSuccess(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.authResetSuccess,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackAuthLogout(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.authLogout,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackProfileNameSave(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.profileNameSave,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }

  public static trackProfilePhotoUpload(
    screenPath: string,
    source: string,
    isAuthenticated?: boolean,
  ): void {
    AnalyticsFeature.track(
      PiAnalyticsEvent.profilePhotoUpload,
      toTrackableParams(buildBaseParams(screenPath, isAuthenticated), {
        action_source: source,
      }),
    )
  }
}
